import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Typography,
    Button,
    Chip,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Stack,
    Alert,
    CircularProgress,
    Tooltip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Menu,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    PlayArrow,
    Close,
    CheckCircle,
    Schedule,
    Warning,
    OndemandVideo,
    AccessTime,
    Star,
    CloudUpload,
    MoreVert,
    Publish,
    Unpublished,
    Delete,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api';

const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
const ALLOWED_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const ACCEPT_ATTR = 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov';

function normalizeVideoMime(file) {
    if (!file) return '';
    const mime = String(file.type || '').toLowerCase().trim();
    if (ALLOWED_TYPES.includes(mime)) return mime;
    const name = String(file.name || '').toLowerCase();
    if (name.endsWith('.mp4')) return 'video/mp4';
    if (name.endsWith('.webm')) return 'video/webm';
    if (name.endsWith('.mov')) return 'video/quicktime';
    return mime;
}

function isAllowedVideoFile(file) {
    if (!file) return false;
    return ALLOWED_TYPES.includes(normalizeVideoMime(file));
}

function canManageSafetyVideos(user) {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'company_admin') return true;
    return user.role === 'company_user' && Boolean(user.isVideoAdmin);
}

const CATEGORIES = [
    { value: 'general_safety', label: 'General Safety' },
    { value: 'moffett_training', label: 'Moffett Training' },
    { value: 'truss_trailer', label: 'Truss Trailer' },
    { value: 'load_securing', label: 'Load Securing' },
    { value: 'equipment_operation', label: 'Equipment Operation' },
    { value: 'emergency_procedures', label: 'Emergency Procedures' },
    { value: 'compliance', label: 'Compliance' },
    { value: 'best_practices', label: 'Best Practices' },
];

const SafetyVideoLibrary = ({ searchTerm, filterCategory }) => {
    const { user } = useAuth();
    const isManager = canManageSafetyVideos(user);

    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [playbackUrl, setPlaybackUrl] = useState('');
    const [playbackMime, setPlaybackMime] = useState('video/mp4');
    const [playbackError, setPlaybackError] = useState('');
    const [videoDialogOpen, setVideoDialogOpen] = useState(false);
    const [error, setError] = useState('');
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadForm, setUploadForm] = useState({
        title: '',
        description: '',
        category: 'general_safety',
        ready: false,
        file: null,
    });
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuVideo, setMenuVideo] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const lastProgressSent = useRef(0);
    const videoRef = useRef(null);

    useEffect(() => {
        fetchVideos();
    }, [searchTerm, filterCategory, isManager]);

    const fetchVideos = async () => {
        try {
            setLoading(true);
            setError('');
            const params = new URLSearchParams({
                type: 'video',
                ...(searchTerm && { search: searchTerm }),
                ...(filterCategory && { category: filterCategory }),
            });
            if (!isManager) {
                params.set('ready', 'true');
            }

            const response = await apiClient.get(`/safety/content?${params}`);
            let items = response.data.data || [];
            if (!isManager) {
                items = items.filter((v) => v.ready || v.isPublished);
            }
            setVideos(items);
        } catch (err) {
            console.error('Error fetching videos:', err);
            setError(err.response?.data?.message || 'Failed to load videos');
        } finally {
            setLoading(false);
        }
    };

    const trackVideoProgress = async (videoId, progressData) => {
        try {
            await apiClient.post(`/safety/tracking/${videoId}`, progressData);
        } catch (err) {
            console.error('Error tracking video progress:', err);
        }
    };

    const handleVideoClick = async (video) => {
        setSelectedVideo(video);
        setPlaybackUrl('');
        setPlaybackMime(video.mimeType || 'video/mp4');
        setPlaybackError('');
        setVideoDialogOpen(true);
        lastProgressSent.current = 0;
        trackVideoProgress(video._id, { status: 'in_progress' });

        try {
            const res = await apiClient.get(`/safety/content/${video._id}/playback-url`);
            setPlaybackUrl(res.data?.data?.playbackUrl || '');
            if (res.data?.data?.mimeType) {
                setPlaybackMime(res.data.data.mimeType);
            }
        } catch (err) {
            console.error('Playback URL error:', err);
            setPlaybackError(err.response?.data?.message || 'Unable to load video playback');
            setError(err.response?.data?.message || 'Unable to load video playback');
        }
    };

    const handleClosePlayer = () => {
        setVideoDialogOpen(false);
        setSelectedVideo(null);
        setPlaybackUrl('');
        setPlaybackMime('video/mp4');
        setPlaybackError('');
        fetchVideos();
    };

    const handleMediaError = () => {
        const mime = String(playbackMime || selectedVideo?.mimeType || '').toLowerCase();
        if (mime.includes('quicktime') || /\.mov($|\?)/i.test(playbackUrl)) {
            setPlaybackError(
                'This browser cannot play this MOV/QuickTime file. Re-upload as MP4 (H.264) for Chrome/Edge playback.'
            );
            return;
        }
        setPlaybackError('Unable to play this video in your browser. Try uploading an MP4 (H.264) file.');
    };

    const handleVideoComplete = (video) => {
        trackVideoProgress(video._id, {
            status: 'completed',
            progressPercentage: 100,
        });
        handleClosePlayer();
    };

    const handleTimeUpdate = () => {
        const el = videoRef.current;
        if (!el || !selectedVideo || !el.duration) return;
        const pct = Math.round((el.currentTime / el.duration) * 100);
        const now = Date.now();
        if (now - lastProgressSent.current < 5000 && pct < 90) return;
        lastProgressSent.current = now;
        trackVideoProgress(selectedVideo._id, {
            status: pct >= 90 ? 'completed' : 'in_progress',
            progressPercentage: pct,
            watchTimeSeconds: Math.floor(el.currentTime),
        });
    };

    const selectUploadFile = (file) => {
        if (!file) {
            setUploadForm((p) => ({ ...p, file: null }));
            return;
        }
        if (!isAllowedVideoFile(file)) {
            setUploadError('Only MP4, WebM, and MOV files are allowed');
            return;
        }
        if (file.size > MAX_VIDEO_BYTES) {
            setUploadError('File must be 500MB or smaller');
            return;
        }
        setUploadError('');
        setUploadForm((p) => ({ ...p, file }));
    };

    const handleUploadSubmit = async () => {
        setUploadError('');
        if (!uploadForm.title.trim()) {
            setUploadError('Title is required');
            return;
        }
        if (!uploadForm.file) {
            setUploadError('Choose a video file');
            return;
        }
        const mimeType = normalizeVideoMime(uploadForm.file);
        if (!ALLOWED_TYPES.includes(mimeType)) {
            setUploadError('Only MP4, WebM, and MOV files are allowed');
            return;
        }
        if (uploadForm.file.size > MAX_VIDEO_BYTES) {
            setUploadError('File must be 500MB or smaller');
            return;
        }

        setUploading(true);
        try {
            const createRes = await apiClient.post('/safety/content', {
                title: uploadForm.title.trim(),
                description: uploadForm.description.trim() || 'Training video',
                category: uploadForm.category,
                type: 'video',
                ready: false,
            });
            const content = createRes.data.data;
            const contentId = content._id;

            const urlRes = await apiClient.post(`/safety/content/${contentId}/upload-url`, {
                contentType: mimeType,
                filename: uploadForm.file.name,
            });
            const { uploadUrl } = urlRes.data.data;

            const putRes = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': mimeType },
                body: uploadForm.file,
            });
            if (!putRes.ok) {
                throw new Error(`S3 upload failed (${putRes.status})`);
            }

            await apiClient.post(`/safety/content/${contentId}/upload-complete`, {
                fileSize: uploadForm.file.size,
                mimeType,
            });

            if (uploadForm.ready) {
                await apiClient.patch(`/safety/content/${contentId}/ready`, { ready: true });
            }

            setUploadOpen(false);
            setUploadForm({
                title: '',
                description: '',
                category: 'general_safety',
                ready: false,
                file: null,
            });
            setDragActive(false);
            await fetchVideos();
        } catch (err) {
            console.error('Upload failed:', err);
            setUploadError(err.response?.data?.message || err.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const openMenu = (e, video) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setMenuVideo(video);
    };

    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuVideo(null);
    };

    const setReady = async (video, ready) => {
        closeMenu();
        try {
            await apiClient.patch(`/safety/content/${video._id}/ready`, { ready });
            await fetchVideos();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update Ready state');
        }
    };

    const softDelete = async (video) => {
        closeMenu();
        try {
            await apiClient.delete(`/safety/content/${video._id}`);
            await fetchVideos();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete video');
        }
    };

    const getStatusIcon = (tracking) => {
        if (!tracking) return <PlayArrow color="primary" />;
        switch (tracking.status) {
            case 'completed':
                return <CheckCircle color="success" />;
            case 'in_progress':
                return <Schedule color="warning" />;
            case 'overdue':
                return <Warning color="error" />;
            default:
                return <PlayArrow color="primary" />;
        }
    };

    const getStatusColor = (tracking) => {
        if (!tracking) return 'default';
        switch (tracking.status) {
            case 'completed':
                return 'success';
            case 'in_progress':
                return 'warning';
            case 'overdue':
                return 'error';
            default:
                return 'default';
        }
    };

    const getCategoryLabel = (category) =>
        CATEGORIES.find((c) => c.value === category)?.label || category;

    const formatDuration = (seconds) => {
        if (!seconds) return '';
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
                mb={3}
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    <OndemandVideo color="primary" fontSize="large" />
                    <Typography variant="h5">
                        Safety Training Videos ({videos.length})
                    </Typography>
                </Stack>
                {isManager && (
                    <Button
                        variant="contained"
                        startIcon={<CloudUpload />}
                        onClick={() => setUploadOpen(true)}
                    >
                        Upload video
                    </Button>
                )}
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {videos.length === 0 ? (
                <Alert severity="info">
                    {isManager
                        ? 'No videos yet. Upload a training video to get started.'
                        : 'No ready training videos available yet.'}
                </Alert>
            ) : (
                <Grid container spacing={3}>
                    {videos.map((video) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={video._id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    cursor: video.hasFile ? 'pointer' : 'default',
                                    '&:hover': video.hasFile
                                        ? {
                                              transform: 'translateY(-4px)',
                                              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                          }
                                        : undefined,
                                }}
                                onClick={() => video.hasFile && handleVideoClick(video)}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        height: 180,
                                        flexShrink: 0,
                                        overflow: 'hidden',
                                        bgcolor: 'grey.800',
                                        backgroundImage:
                                            'linear-gradient(145deg, #2c3340 0%, #1a1f29 55%, #12151c 100%)',
                                    }}
                                >
                                    {video.videoThumbnail ? (
                                        <CardMedia
                                            component="img"
                                            height="180"
                                            image={video.videoThumbnail}
                                            alt={video.title}
                                            sx={{
                                                height: 180,
                                                objectFit: 'cover',
                                                position: 'absolute',
                                                inset: 0,
                                            }}
                                            onError={(e) => {
                                                e.currentTarget.style.visibility = 'hidden';
                                            }}
                                        />
                                    ) : null}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            pointerEvents: 'none',
                                            opacity: video.videoThumbnail ? 0 : 0.35,
                                        }}
                                    >
                                        <OndemandVideo sx={{ fontSize: 56, color: 'common.white' }} />
                                    </Box>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            bgcolor: 'rgba(0,0,0,0.28)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                bgcolor: 'rgba(0,0,0,0.72)',
                                                borderRadius: '50%',
                                                width: 56,
                                                height: 56,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'common.white',
                                                '& .MuiSvgIcon-root': { fontSize: 32 },
                                            }}
                                        >
                                            {getStatusIcon(video.userTracking)}
                                        </Box>
                                    </Box>

                                    {video.videoDuration ? (
                                        <Chip
                                            label={formatDuration(video.videoDuration)}
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                bottom: 8,
                                                right: 8,
                                                bgcolor: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                            }}
                                        />
                                    ) : null}

                                    {video.isRequired && (
                                        <Chip
                                            label="Required"
                                            size="small"
                                            color="error"
                                            sx={{ position: 'absolute', top: 8, left: 8 }}
                                        />
                                    )}

                                    {isManager && (
                                        <>
                                            <Chip
                                                label={video.ready || video.isPublished ? 'Ready' : 'Draft'}
                                                size="small"
                                                color={video.ready || video.isPublished ? 'success' : 'default'}
                                                sx={{ position: 'absolute', top: 8, left: video.isRequired ? 88 : 8 }}
                                            />
                                            <IconButton
                                                size="small"
                                                aria-label="Video actions"
                                                onClick={(e) => openMenu(e, video)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    bgcolor: 'rgba(255,255,255,0.9)',
                                                }}
                                            >
                                                <MoreVert fontSize="small" />
                                            </IconButton>
                                        </>
                                    )}

                                    {(video.priority === 'high' || video.priority === 'critical') && (
                                        <Tooltip title={`${video.priority} priority`}>
                                            <Star
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 8,
                                                    left: 8,
                                                    color:
                                                        video.priority === 'critical'
                                                            ? 'error.main'
                                                            : 'warning.main',
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>

                                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{
                                            fontWeight: 'bold',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                        }}
                                    >
                                        {video.title}
                                    </Typography>

                                    <Chip
                                        label={getCategoryLabel(video.category)}
                                        size="small"
                                        variant="outlined"
                                        sx={{ mb: 2, alignSelf: 'flex-start' }}
                                    />

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            flexGrow: 1,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            mb: 2,
                                        }}
                                    >
                                        {video.description}
                                    </Typography>

                                    {video.userTracking && video.userTracking.progressPercentage > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Stack direction="row" justifyContent="space-between" mb={1}>
                                                <Typography variant="caption">Progress</Typography>
                                                <Typography variant="caption">
                                                    {video.userTracking.progressPercentage}%
                                                </Typography>
                                            </Stack>
                                            <LinearProgress
                                                variant="determinate"
                                                value={video.userTracking.progressPercentage}
                                                sx={{ borderRadius: 1 }}
                                            />
                                        </Box>
                                    )}

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Chip
                                            label={
                                                video.userTracking
                                                    ? video.userTracking.status.replace('_', ' ')
                                                    : 'Not Started'
                                            }
                                            size="small"
                                            color={getStatusColor(video.userTracking)}
                                            variant="outlined"
                                        />
                                        {video.userTracking?.lastViewedAt && (
                                            <Tooltip
                                                title={`Last viewed: ${new Date(
                                                    video.userTracking.lastViewedAt
                                                ).toLocaleDateString()}`}
                                            >
                                                <AccessTime fontSize="small" color="action" />
                                            </Tooltip>
                                        )}
                                    </Stack>

                                    {isManager && !video.hasFile && (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            File missing
                                        </Alert>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                {menuVideo && !(menuVideo.ready || menuVideo.isPublished) && (
                    <MenuItem
                        onClick={() => setReady(menuVideo, true)}
                        disabled={!menuVideo.hasFile}
                    >
                        <ListItemIcon>
                            <Publish fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Mark Ready</ListItemText>
                    </MenuItem>
                )}
                {menuVideo && (menuVideo.ready || menuVideo.isPublished) && (
                    <MenuItem onClick={() => setReady(menuVideo, false)}>
                        <ListItemIcon>
                            <Unpublished fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Unpublish</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={() => softDelete(menuVideo)}>
                    <ListItemIcon>
                        <Delete fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>

            {/* Player */}
            <Dialog
                open={videoDialogOpen}
                onClose={handleClosePlayer}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { height: '90vh' } }}
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">{selectedVideo?.title}</Typography>
                        <IconButton aria-label="Close video" onClick={handleClosePlayer}>
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {selectedVideo && (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    bgcolor: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: 320,
                                }}
                            >
                                {playbackError ? (
                                    <Alert severity="warning" sx={{ m: 2, maxWidth: 520 }}>
                                        {playbackError}
                                    </Alert>
                                ) : playbackUrl ? (
                                    <video
                                        key={playbackUrl}
                                        ref={videoRef}
                                        src={playbackUrl}
                                        width="100%"
                                        height="100%"
                                        controls
                                        playsInline
                                        autoPlay
                                        preload="metadata"
                                        style={{ maxHeight: '70vh' }}
                                        onEnded={() => handleVideoComplete(selectedVideo)}
                                        onTimeUpdate={handleTimeUpdate}
                                        onError={handleMediaError}
                                    />
                                ) : (
                                    <CircularProgress sx={{ color: 'white' }} />
                                )}
                            </Box>
                            <Box sx={{ p: 3 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={8}>
                                        <Typography variant="body1" paragraph>
                                            {selectedVideo.description}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Button
                                            variant="contained"
                                            onClick={() => handleVideoComplete(selectedVideo)}
                                            startIcon={<CheckCircle />}
                                            fullWidth
                                        >
                                            Mark as Complete
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Upload */}
            <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload training video</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {uploadError && <Alert severity="error">{uploadError}</Alert>}
                        <TextField
                            label="Title"
                            required
                            fullWidth
                            value={uploadForm.title}
                            onChange={(e) => setUploadForm((p) => ({ ...p, title: e.target.value }))}
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            minRows={2}
                            value={uploadForm.description}
                            onChange={(e) =>
                                setUploadForm((p) => ({ ...p, description: e.target.value }))
                            }
                        />
                        <FormControl fullWidth>
                            <InputLabel id="upload-cat">Category</InputLabel>
                            <Select
                                labelId="upload-cat"
                                label="Category"
                                value={uploadForm.category}
                                onChange={(e) =>
                                    setUploadForm((p) => ({ ...p, category: e.target.value }))
                                }
                            >
                                {CATEGORIES.map((c) => (
                                    <MenuItem key={c.value} value={c.value}>
                                        {c.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box
                            onDragEnter={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!uploading) setDragActive(true);
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!uploading) setDragActive(true);
                            }}
                            onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDragActive(false);
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDragActive(false);
                                if (uploading) return;
                                const file = e.dataTransfer?.files?.[0];
                                selectUploadFile(file);
                            }}
                            onClick={() => !uploading && fileInputRef.current?.click()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    fileInputRef.current?.click();
                                }
                            }}
                            aria-label="Drop or choose a video file"
                            sx={{
                                border: '2px dashed',
                                borderColor: dragActive ? 'primary.main' : 'divider',
                                bgcolor: dragActive ? 'action.hover' : 'background.paper',
                                borderRadius: 2,
                                p: 3,
                                textAlign: 'center',
                                cursor: uploading ? 'default' : 'pointer',
                                transition: 'border-color 0.15s, background-color 0.15s',
                            }}
                        >
                            <CloudUpload color={dragActive ? 'primary' : 'action'} sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="body1" fontWeight={600}>
                                {uploadForm.file
                                    ? uploadForm.file.name
                                    : dragActive
                                      ? 'Drop video here'
                                      : 'Drag & drop a video here'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                or click to choose · Prefer MP4 (H.264) · MOV may not play in Chrome · max 500MB
                            </Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept={ACCEPT_ATTR}
                                onChange={(e) => selectUploadFile(e.target.files?.[0] || null)}
                            />
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={uploadForm.ready}
                                    onChange={(e) =>
                                        setUploadForm((p) => ({ ...p, ready: e.target.checked }))
                                    }
                                />
                            }
                            label="Mark Ready after upload (visible to company users)"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadOpen(false)} disabled={uploading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUploadSubmit}
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={18} /> : <CloudUpload />}
                    >
                        {uploading ? 'Uploading…' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SafetyVideoLibrary;
